import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation } from 'react-router'

import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'

import {
  DeliveryInfoInput,
  DiningOptionBehavior,
  FulfillmentType,
  useRestaurantSchedulesQuery,
  useUpdateFulfillmentMutation
} from 'src/apollo/onlineOrdering'
import { DiningBehavior } from 'src/apollo/sites'
import {
  cleanDeliveryInfo,
  getDefaultFulfillmentInfo,
  getPersistedDeliveryInfo
} from 'src/public/components/online_ordering/fulfillmentUtils'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import {
  DiningOptions,
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  CartErrorType,
  useCart
} from 'public/components/online_ordering/CartContext'
import { ScheduleType } from 'public/components/online_ordering/types'

import { useDelivery } from './DeliveryContext'
import { schedulesByGuid } from './scheduleUtils'
import { alertSuccess } from 'src/shared/js/alertUtils'
import { t } from 'src/translations'
import ToastProduct from './ToastProduct'

export const DELIVERY_INFO_STORAGE_KEY = 'fulfillmentDeliveryInfo'

export type FutureFulfillmentDates = NonNullable<
  NonNullable<NonNullable<DiningOptions>[0]>['futureSchedule']
>['dates']

// composite type that extends DeliveryInfoInput with optional SavedAddress properties
// so that children can derive the cart's address classification of new vs saved
export type CartDeliveryInfo = DeliveryInfoInput & {
  guid?: string
  name?: string | null
}

export type CartFulfillmentData = {
  fulfillmentType: FulfillmentType
  fulfillmentDateTime?: string | null
  diningOptionBehavior?: DiningOptionBehavior
  deliveryInfo?: CartDeliveryInfo | null
}

type FulfillmentSchedules = {
  asapAvailableNow: boolean
  futureScheduleDates: FutureFulfillmentDates
}

export type FulfillmentData = {
  cartFulfillmentData: CartFulfillmentData
  scheduleData: FulfillmentSchedules
}

export type Availability = {
  asapAvailable: boolean
  scheduledAvailable: boolean
}

export type FulfillmentContextType = {
  // Rx config data
  takeoutEnabled: boolean
  deliveryEnabled: boolean
  canOrderTakeout: boolean
  canOrderDelivery: boolean
  canUpdateFulfillment: boolean
  // From cart or default values
  fulfillmentData?: FulfillmentData
  setDefaultDiningOptionBehavior: (
    behavior: DiningOptionBehavior | null
  ) => void
  selectedFulfillmentType: FulfillmentType | null
  setSelectedFulfillmentType: (type: FulfillmentType | null) => void
  selectedDiningOptionBehavior: DiningOptionBehavior | null
  setSelectedDiningOptionBehavior: (
    behavior: DiningOptionBehavior | null
  ) => void
  selectedDiningOptionBehaviorSchedules: FulfillmentSchedules | null
  canScheduleSelectedBehavior: boolean
  diningOptionBehaviorAvailability: { [key: string]: Availability }
  // Update cart fulfillment data
  updateCartFulfillment: (
    cartFulfillmentData: CartFulfillmentData
  ) => Promise<boolean>
  loadingFulfillment: boolean
  fulfillmentError: CartErrorType | null
  guestSelectedFulfillmentTime: boolean
  setGuestSelectedFulfillmentTime: (
    guestSelectedFulfillmentTime: boolean
  ) => void
  fulfillmentTimeModalOpen: boolean
  setFulfillmentTimeModalOpen: (open: boolean) => Promise<void>
  forceFulfillmentSelection: boolean
  setForceFulfillmentSelection: (forceFulfillmentSelection: boolean) => void
}

// Find the day and time from fulfillmentDateTime in the futureScheduleDates and return indexes where each is located.
// Returns nulls if the inputs are not valid.
export const getDateTimeIndexes = (
  fulfillmentDateTime?: string | null,
  futureScheduleDates?: FutureFulfillmentDates
) => {
  const existingFulfillmentDateTime = fulfillmentDateTime
    ? parseISO(fulfillmentDateTime)
    : null
  if (
    !existingFulfillmentDateTime ||
    (futureScheduleDates?.length || 0) === 0
  ) {
    return [null, null]
  }

  const date = format(existingFulfillmentDateTime, 'yyyy-MM-dd')
  const dayIndex = futureScheduleDates!.findIndex((d) => d.date === date)
  const overnightDayIndex = dayIndex > 0 ? dayIndex - 1 : -1

  const existingDateTime = existingFulfillmentDateTime.getTime()
  const timeIndex =
    futureScheduleDates![dayIndex]?.times.findIndex(
      (t) => parseISO(t.time).getTime() === existingDateTime
    ) || 0

  // If we couldn't find the time in today's values, try yesterday for times past midnight but before the cutoff
  const overnightTimeIndex =
    futureScheduleDates![overnightDayIndex]?.times.findIndex(
      (t) => parseISO(t.time).getTime() === existingDateTime
    ) || 0

  // If we couldn't find the time in today or yesterday, return the first available day and time
  if (timeIndex === -1 && overnightTimeIndex === -1) {
    const firstAvailableDayIndex = futureScheduleDates!.findIndex(
      (date) => date.times.length > 0
    )
    return [firstAvailableDayIndex, 0]
  }

  return timeIndex === -1
    ? [overnightDayIndex, overnightTimeIndex]
    : [dayIndex, timeIndex]
}

export const getDiningOptionBehaviorAvailability = (
  diningOptions?: DiningOptions
) => {
  const takeoutOptions = diningOptions?.find(
    (option) => option.behavior === DiningOptionBehavior.TakeOut
  )
  const deliveryOptions = diningOptions?.find(
    (option) => option.behavior === DiningOptionBehavior.Delivery
  )

  return {
    takeoutEnabled: Boolean(takeoutOptions),
    deliveryEnabled: Boolean(deliveryOptions),
    canOrderTakeout: Boolean(
      takeoutOptions?.asapSchedule?.availableNow ||
        takeoutOptions?.futureSchedule?.dates?.filter(
          (date) => date?.times?.length
        )?.length
    ),
    canOrderDelivery: Boolean(
      deliveryOptions?.asapSchedule?.availableNow ||
        deliveryOptions?.futureSchedule?.dates?.filter(
          (date) => date?.times?.length
        )?.length
    )
  }
}

export const useLocationSchedules = () => {
  const { locations } = useRestaurant()
  const ooClient = useOOClient()
  const locationsGuids = locations?.map((l) => l.externalId) || []
  const { data: scheduleData } = useRestaurantSchedulesQuery({
    variables: { restaurantGuids: locationsGuids },
    skip: !locationsGuids || locationsGuids.length === 0,
    client: ooClient
  })

  return useMemo(() => schedulesByGuid(scheduleData), [scheduleData]) as {
    [key: string]: ScheduleType
  }
}

export const useOOLocations = () => {
  const {
    selectedLocation,
    locations,
    toastProduct,
    isEcommMultiLocationEnabled,
    ecommSettings
  } = useRestaurant()
  const locationSchedules = useLocationSchedules()

  const ooLocations =
    locations?.filter(
      (l) => locationSchedules[l.externalId]?.onlineOrderingEnabled
    ) || []

  return useMemo(() => {
    if (toastProduct !== ToastProduct.OOEcommerce) {
      return ooLocations
    }

    // If MLM is not enabled or no locations are configured, return the selected location
    if (
      !isEcommMultiLocationEnabled ||
      ecommSettings?.enabledRestaurantGuids?.length === 0
    ) {
      return [selectedLocation]
    }

    // Filter OO locations to only those enabled for ecomm MLM
    return ooLocations.filter((location) =>
      ecommSettings?.enabledRestaurantGuids?.includes(location.externalId)
    )
  }, [
    toastProduct,
    isEcommMultiLocationEnabled,
    ecommSettings?.enabledRestaurantGuids,
    ooLocations,
    selectedLocation
  ])
}

export const use3pdConfig = () => {
  const locationSchedules = useLocationSchedules()
  const { selectedLocation } = useRestaurant()

  const deliveryServiceAvailability = useMemo(
    () =>
      locationSchedules[selectedLocation.externalId]
        ?.deliveryServiceAvailability?.available ?? false,
    [locationSchedules, selectedLocation]
  )

  return { uses3pd: deliveryServiceAvailability }
}

export const FulfillmentContext = createContext<
  FulfillmentContextType | undefined
>(undefined)

/** A dummy/empty fulfillment context provider that users cannot interact with. */
export const EditorFulfillmentContextProvider = (
  props: React.PropsWithChildren<{}>
) => {
  // The only thing the editor really needs to know is whether both takeout and delivery are enabled for the Rx,
  // so we can render the fulfillment control appropriately
  const { diningOptions } = useRestaurant()
  const fulfillmentData: FulfillmentData = {
    cartFulfillmentData: {
      fulfillmentType: FulfillmentType.Asap,
      diningOptionBehavior: DiningOptionBehavior.TakeOut
    },
    scheduleData: { asapAvailableNow: true, futureScheduleDates: [] }
  }
  const selectedFulfillmentType = FulfillmentType.Asap

  return (
    <FulfillmentContext.Provider
      value={{
        ...getDiningOptionBehaviorAvailability(diningOptions),
        canUpdateFulfillment: true,
        fulfillmentData,
        setDefaultDiningOptionBehavior: () => undefined,
        selectedFulfillmentType,
        setSelectedFulfillmentType: () => undefined,
        selectedDiningOptionBehavior: DiningOptionBehavior.TakeOut,
        setSelectedDiningOptionBehavior: () => undefined,
        selectedDiningOptionBehaviorSchedules: null,
        canScheduleSelectedBehavior: true,
        diningOptionBehaviorAvailability: {},
        updateCartFulfillment: () => Promise.resolve(true),
        loadingFulfillment: false,
        fulfillmentError: null,
        guestSelectedFulfillmentTime: true,
        setGuestSelectedFulfillmentTime: () => undefined,
        fulfillmentTimeModalOpen: false,
        setFulfillmentTimeModalOpen: () => Promise.resolve(undefined),
        forceFulfillmentSelection: false,
        setForceFulfillmentSelection: () => undefined
      }}
    >
      {props.children}
    </FulfillmentContext.Provider>
  )
}

export const FulfillmentContextProvider = (
  props: React.PropsWithChildren<{}>
) => {
  const ooClient = useOOClient()
  const { lastKnownValidDeliveryAddress, setLastKnownValidDeliveryAddress } =
    useDelivery()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const { diningOptions, restaurant, refetchOORestaurant } = useRestaurant()
  const { cart, cartGuid, refetchCart } = useCart()
  const { uses3pd } = use3pdConfig()

  const diningOptionsByBehavior = useMemo(
    () => ({
      takeout: diningOptions?.find(
        (opt) => opt.behavior === DiningOptionBehavior.TakeOut
      ),
      delivery: diningOptions?.find(
        (opt) => opt.behavior === DiningOptionBehavior.Delivery
      )
    }),
    [diningOptions]
  )

  const paramDiningOption = queryParams.get(
    'diningOption'
  ) as DiningBehavior | null

  const rxDefaultDiningBehavior = paramDiningOption
    ? diningOptionsByBehavior[paramDiningOption]
    : restaurant.config.ooConfig?.defaultDiningBehavior
    ? diningOptionsByBehavior[restaurant.config.ooConfig?.defaultDiningBehavior]
    : null

  const [defaultFulfillment, setDefaultFulfillment] = useState<
    FulfillmentData | undefined
  >(() => {
    const fulfillmentAddress =
      lastKnownValidDeliveryAddress ??
      getPersistedDeliveryInfo(DELIVERY_INFO_STORAGE_KEY)

    return (
      getDefaultFulfillmentInfo(
        rxDefaultDiningBehavior,
        diningOptionsByBehavior,
        fulfillmentAddress
      ) ?? undefined
    )
  })

  const [defaultDiningOptionBehavior, setDefaultDiningOptionBehavior] =
    useState<DiningOptionBehavior | null>(null)
  const [selectedFulfillmentType, setSelectedFulfillmentType] =
    useState<FulfillmentType | null>(null)
  const [selectedDiningOptionBehavior, setSelectedDiningOptionBehavior] =
    useState<DiningOptionBehavior | null>(null)
  const [updateFulfillmentMutation] = useUpdateFulfillmentMutation({
    client: ooClient
  })
  const [loadingFulfillment, setLoadingFulfillment] = useState(false)
  const [fulfillmentError, setFulfillmentError] =
    useState<CartErrorType | null>(null)
  const [guestSelectedFulfillmentTime, setGuestSelectedFulfillmentTime] =
    useState(false) // has the guest actively selected a fulfillment time?
  const [fulfillmentTimeModalOpen, _setFulfillmentTimeModalOpen] =
    useState(false)
  const [lastFulfillmentFetch, setLastFulfillmentFetch] = useState<Date>(
    new Date()
  )
  const [forceFulfillmentSelection, setForceFulfillmentSelection] =
    useState(false)

  const setFulfillmentTimeModalOpen = useCallback(
    async (open: boolean) => {
      if (open) {
        // Fulfillment times grow stale at 0, 15, 30, and 45 minutes past the hour since we get future schedules in 15 minute intervals.
        // Refetch the rx if we've passed one of those times since the last fetch so futureScheduleDates won't be outdated.
        const minutesUntilStale = 15 - (lastFulfillmentFetch.getMinutes() % 15)
        const staleTime = new Date(
          new Date(
            lastFulfillmentFetch.getTime() + minutesUntilStale * 60 * 1000
          ).setSeconds(0)
        )
        const now = new Date()
        if (now > staleTime) {
          await refetchOORestaurant()
          setLastFulfillmentFetch(now)
        }
      }
      _setFulfillmentTimeModalOpen(open)
    },
    [
      lastFulfillmentFetch,
      setLastFulfillmentFetch,
      refetchOORestaurant,
      _setFulfillmentTimeModalOpen
    ]
  )

  const canUpdateFulfillment = useMemo(
    () =>
      // Allow updating fulfillment if any of the editable fields in the fulfillment modal can be changed.
      // This should be true if the following are editable:
      // 1) You can edit the future takeout time
      // 2) You can edit the future delivery time
      // 3) You can switch between takeout and delivery
      // 4) You can edit the delivery address
      Boolean(
        diningOptionsByBehavior.takeout?.futureSchedule?.dates?.length ||
          diningOptionsByBehavior.delivery?.futureSchedule?.dates?.length ||
          diningOptionsByBehavior.delivery?.asapSchedule?.availableNow
      ),
    [diningOptionsByBehavior]
  )

  // Re-compute default fulfillment when dining options change (e.g. Rx location change)
  useEffect(() => {
    const fulfillmentAddress =
      lastKnownValidDeliveryAddress ??
      getPersistedDeliveryInfo(DELIVERY_INFO_STORAGE_KEY)

    const newDefault = getDefaultFulfillmentInfo(
      rxDefaultDiningBehavior,
      diningOptionsByBehavior,
      fulfillmentAddress
    )

    if (newDefault) {
      setDefaultFulfillment(newDefault)
    }

    // only reset default data when the options change (i.e. the Rx changes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diningOptionsByBehavior])

  const checkLocationChanged = useRef(false)

  // Render an alert message if the location has just changed
  useEffect(() => {
    const locationChangedParam = queryParams.get('locationChanged')
    if (locationChangedParam && !checkLocationChanged.current) {
      checkLocationChanged.current = true
      const locationName = decodeURIComponent(locationChangedParam)
      alertSuccess(t('change-location-alert.success', { locationName }))

      setTimeout(() => {
        // remove the locationChanged param from the URL so the message doesn't show again on refresh
        queryParams.delete('locationChanged')
        const newPath = location.pathname
        if (newPath) {
          window.history.replaceState({}, '', newPath)
        }
      }, 3000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Returns false if an error occurred, otherwise true
  const updateCartFulfillment = useCallback(
    async (cartFulfillmentData: CartFulfillmentData) => {
      setFulfillmentError(null)

      if (cartGuid) {
        // Only include the exact properties that DeliveryInfoInput expects - this is a bit silly.
        // TODO: is schema enforced server side and could be relaxed?
        const cleanedCartFulfillmentData = {
          ...cartFulfillmentData,
          deliveryInfo: cleanDeliveryInfo(cartFulfillmentData.deliveryInfo)
        }

        setLoadingFulfillment(true)

        let response: Awaited<ReturnType<typeof updateFulfillmentMutation>>

        try {
          // eslint-disable-next-line max-len
          response = await updateFulfillmentMutation({
            variables: {
              input: {
                cartGuid,
                cartFulfillmentInput: cleanedCartFulfillmentData,
                skipPrecheckoutValidation: !(
                  uses3pd && cartFulfillmentData.deliveryInfo
                )
              }
            }
          })
        } catch (error) {
          setLoadingFulfillment(false)
          throw error
        }
        await refetchCart()
        setLoadingFulfillment(false)

        if (
          response.data?.updateFulfillmentAndValidate.__typename ===
            'CartModificationError' ||
          response.data?.updateFulfillmentAndValidate.__typename ===
            'CartOutOfStockError'
        ) {
          setFulfillmentError({
            kind: response.data?.updateFulfillmentAndValidate.__typename,
            message: response.data?.updateFulfillmentAndValidate.message
          })
          return false
        }
      } else {
        setDefaultFulfillment({
          cartFulfillmentData,
          scheduleData: {
            asapAvailableNow:
              (cartFulfillmentData.diningOptionBehavior ===
              DiningOptionBehavior.Delivery
                ? diningOptionsByBehavior.delivery?.asapSchedule?.availableNow
                : diningOptionsByBehavior.takeout?.asapSchedule
                    ?.availableNow) || false,
            futureScheduleDates:
              (cartFulfillmentData.diningOptionBehavior ===
              DiningOptionBehavior.Delivery
                ? diningOptionsByBehavior.delivery?.futureSchedule?.dates
                : diningOptionsByBehavior.takeout?.futureSchedule?.dates) || []
          }
        })
      }

      // Remove persisted deliveryInfo once we've set fulfillment details
      if (localStorage.getItem(DELIVERY_INFO_STORAGE_KEY)) {
        localStorage.removeItem(DELIVERY_INFO_STORAGE_KEY)
      }

      return true
    },
    [
      cartGuid,
      refetchCart,
      updateFulfillmentMutation,
      uses3pd,
      diningOptionsByBehavior.delivery?.asapSchedule?.availableNow,
      diningOptionsByBehavior.delivery?.futureSchedule?.dates,
      diningOptionsByBehavior.takeout?.asapSchedule?.availableNow,
      diningOptionsByBehavior.takeout?.futureSchedule?.dates
    ]
  )

  const fulfillmentData = useMemo(() => {
    const fulfillment = cart
      ? {
          cartFulfillmentData: {
            fulfillmentType: cart.fulfillmentType,
            fulfillmentDateTime: cart.fulfillmentDateTime,
            diningOptionBehavior: cart.diningOptionBehavior,
            deliveryInfo: cart.order?.deliveryInfo as DeliveryInfoInput
          },
          scheduleData: {
            asapAvailableNow:
              (cart.diningOptionBehavior === DiningOptionBehavior.Delivery
                ? diningOptionsByBehavior.delivery?.asapSchedule?.availableNow
                : diningOptionsByBehavior.takeout?.asapSchedule
                    ?.availableNow) || false,
            futureScheduleDates:
              (cart.diningOptionBehavior === DiningOptionBehavior.Delivery
                ? diningOptionsByBehavior.delivery?.futureSchedule?.dates
                : diningOptionsByBehavior.takeout?.futureSchedule?.dates) || []
          }
        }
      : defaultFulfillment

    // When the Rx location and fulfillment are updated at the same time,
    // defaultDiningOptionBehavior allows the behavior to be set after the Rx and cart
    // have updated
    if (fulfillment && defaultDiningOptionBehavior) {
      fulfillment.cartFulfillmentData.diningOptionBehavior =
        defaultDiningOptionBehavior
      fulfillment.scheduleData = {
        asapAvailableNow:
          (defaultDiningOptionBehavior === DiningOptionBehavior.Delivery
            ? diningOptionsByBehavior.delivery?.asapSchedule?.availableNow
            : diningOptionsByBehavior.takeout?.asapSchedule?.availableNow) ||
          false,
        futureScheduleDates:
          (defaultDiningOptionBehavior === DiningOptionBehavior.Delivery
            ? diningOptionsByBehavior.delivery?.futureSchedule?.dates
            : diningOptionsByBehavior.takeout?.futureSchedule?.dates) || []
      }
      setDefaultDiningOptionBehavior(null)
    }
    if (cart?.fulfillmentDateTime) {
      setGuestSelectedFulfillmentTime(true)
    }

    return fulfillment
    // only update when the cart is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, defaultFulfillment])

  // Reset the selected behavior, lastKnownValidDeliveryAddress, and fulfillment type when the cart is updated
  useEffect(() => {
    const behavior = fulfillmentData?.cartFulfillmentData.diningOptionBehavior
    setSelectedDiningOptionBehavior(behavior || null)
    if (behavior === DiningOptionBehavior.Delivery) {
      setLastKnownValidDeliveryAddress(
        cleanDeliveryInfo(
          fulfillmentData?.cartFulfillmentData?.deliveryInfo ?? null
        )
      )
    }
  }, [
    fulfillmentData?.cartFulfillmentData?.deliveryInfo,
    fulfillmentData?.cartFulfillmentData.diningOptionBehavior,
    setLastKnownValidDeliveryAddress
  ])
  useEffect(
    () =>
      setSelectedFulfillmentType(
        fulfillmentData?.cartFulfillmentData.fulfillmentType || null
      ),
    [fulfillmentData?.cartFulfillmentData.fulfillmentType]
  )

  const selectedDiningOptionBehaviorSchedules = useMemo(
    () => ({
      asapAvailableNow:
        (selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
          ? diningOptionsByBehavior.delivery?.asapSchedule?.availableNow
          : diningOptionsByBehavior.takeout?.asapSchedule?.availableNow) ||
        false,
      futureScheduleDates:
        (selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
          ? diningOptionsByBehavior.delivery?.futureSchedule?.dates
          : diningOptionsByBehavior.takeout?.futureSchedule?.dates) || []
    }),
    [selectedDiningOptionBehavior, diningOptionsByBehavior]
  )

  const canScheduleSelectedBehavior = useMemo(
    () =>
      (selectedDiningOptionBehaviorSchedules?.futureScheduleDates?.length ||
        0) > 0,
    [selectedDiningOptionBehaviorSchedules?.futureScheduleDates]
  )

  const diningOptionBehaviorAvailability = useMemo(() => {
    return {
      delivery: {
        asapAvailable: Boolean(
          diningOptionsByBehavior?.delivery?.asapSchedule?.availableNow
        ),
        scheduledAvailable: Boolean(
          diningOptionsByBehavior?.delivery?.futureSchedule?.dates?.length
        )
      },
      takeout: {
        asapAvailable: Boolean(
          diningOptionsByBehavior?.takeout?.asapSchedule?.availableNow
        ),
        scheduledAvailable: Boolean(
          diningOptionsByBehavior?.takeout?.futureSchedule?.dates?.length
        )
      }
    }
  }, [diningOptionsByBehavior])

  return (
    <FulfillmentContext.Provider
      value={{
        ...getDiningOptionBehaviorAvailability(diningOptions),
        canUpdateFulfillment,
        fulfillmentData,
        setDefaultDiningOptionBehavior,
        selectedFulfillmentType,
        setSelectedFulfillmentType,
        selectedDiningOptionBehavior,
        setSelectedDiningOptionBehavior,
        selectedDiningOptionBehaviorSchedules,
        canScheduleSelectedBehavior,
        diningOptionBehaviorAvailability,
        updateCartFulfillment,
        loadingFulfillment,
        fulfillmentError,
        guestSelectedFulfillmentTime,
        setGuestSelectedFulfillmentTime,
        fulfillmentTimeModalOpen,
        setFulfillmentTimeModalOpen,
        forceFulfillmentSelection,
        setForceFulfillmentSelection
      }}
    >
      {props.children}
    </FulfillmentContext.Provider>
  )
}

export const useFulfillment = () => {
  const context = useContext(FulfillmentContext)
  if (!context) {
    throw new Error(
      'useFulfillment must be used within a FulfillmentContextProvider'
    )
  }

  return context
}

export const useOptionalFulfillment = () => {
  return useContext(FulfillmentContext)
}
